import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  NumberInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useMyPermissions } from "../permissions/useMyPermissions";
import { serverErrorText } from "./serverError";
import { useResetRetryBudget } from "./useResetRetryBudget";
import { useRetryBudget } from "./useRetryBudget";
import { useSetRetryBudget } from "./useSetRetryBudget";
import type { RetryBudgetResponse } from "./api";

// Право на изменение настроек организации (администратор и владелец).
const SETTINGS_ORG_MANAGE = "settings.org.manage";

// Секция бюджета повторов организации: действующее значение и его
// источник, поле ввода, «Сохранить» (PUT) и «Сбросить на заводское»
// (DELETE). Если сохранённое значение перестало проходить схему, под
// строкой действующего значения — плашка с самим значением и причиной.
export function RetryBudgetSection(): ReactElement {
  const { t } = useTranslation();
  const budget = useRetryBudget();

  if (budget.isPending) {
    return <Loader />;
  }
  if (budget.isError) {
    return (
      <Alert color="red" title={t("settings.errorHeading")}>
        {t("settings.retries.loadError")}
      </Alert>
    );
  }
  // `key` по сохранённому и действующему значению: после save/reset
  // сервер отдаёт новое состояние → тело перемонтируется, черновик
  // берёт его без setState-в-effect.
  return (
    <RetryBudgetBody
      key={JSON.stringify([
        budget.data.org_max_retries,
        budget.data.effective_max_retries,
      ])}
      budget={budget.data}
    />
  );
}

// Источник значения для бейджа: откат на заводское при недопустимом
// сохранённом, иначе значение организации или заводское.
function sourceBadge(budget: RetryBudgetResponse): {
  readonly color: string;
  readonly labelKey: string;
} {
  if (budget.org_max_retries_invalid_reason !== null) {
    return { color: "orange", labelKey: "settings.retries.sourceFallback" };
  }
  if (budget.org_max_retries !== null) {
    return { color: "blue", labelKey: "settings.retries.sourceOverride" };
  }
  return { color: "gray", labelKey: "settings.retries.sourceDefault" };
}

// Начальный черновик: сохранённое число, если оно допустимо, иначе
// действующее значение.
function initialDraft(budget: RetryBudgetResponse): number {
  const stored = budget.org_max_retries;
  return typeof stored === "number" &&
    budget.org_max_retries_invalid_reason === null
    ? stored
    : budget.effective_max_retries;
}

function RetryBudgetBody({
  budget,
}: {
  readonly budget: RetryBudgetResponse;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useMyPermissions();
  const setMutation = useSetRetryBudget();
  const resetMutation = useResetRetryBudget();
  const [draft, setDraft] = useState<number | string>(initialDraft(budget));

  const readOnly = permissions.isDenied(SETTINGS_ORG_MANAGE);
  const invalidReason = budget.org_max_retries_invalid_reason;
  const badge = sourceBadge(budget);
  const mutationError = setMutation.error ?? resetMutation.error;
  const isOverride = budget.org_max_retries !== null;
  // Сохранять есть что только при числовом черновике: пустое поле
  // NumberInput отдаёт строку, и тогда кнопка отключена.
  const saveDraft =
    typeof draft === "number"
      ? () => {
          resetMutation.reset();
          setMutation.mutate({ max_retries: draft });
        }
      : undefined;

  return (
    <Stack gap="md">
      <Title order={2}>{t("settings.retries.title")}</Title>
      <Text c="dimmed">{t("settings.retries.intro")}</Text>

      <Group gap="xs">
        <Text size="sm" fw={600}>
          {t("settings.retries.currentLabel")}
        </Text>
        <Text size="sm" ff="monospace">
          {String(budget.effective_max_retries)}
        </Text>
        <Badge variant="light" color={badge.color}>
          {t(badge.labelKey)}
        </Badge>
      </Group>
      <Text size="sm">
        {budget.effective_max_retries === 0
          ? t("settings.retries.policyNone")
          : t("settings.retries.policyRetries", {
              count: budget.effective_max_retries,
            })}
      </Text>

      {invalidReason !== null ? (
        <Alert color="orange" title={t("settings.retries.invalidTitle")}>
          {t("settings.retries.invalidStored", {
            value: JSON.stringify(budget.org_max_retries),
            reason: invalidReason,
          })}
        </Alert>
      ) : null}

      <NumberInput
        label={t("settings.retries.fieldLabel")}
        description={t("settings.retries.fieldHint", {
          min: budget.min,
          max: budget.max,
        })}
        value={draft}
        min={budget.min}
        max={budget.max}
        allowDecimal={false}
        clampBehavior="strict"
        disabled={readOnly}
        error={
          mutationError !== null ? serverErrorText(mutationError, t) : undefined
        }
        onChange={setDraft}
      />
      {readOnly ? (
        <Text size="sm" c="dimmed">
          {t("settings.retries.readOnly")}
        </Text>
      ) : null}

      <Group>
        <Button
          onClick={saveDraft}
          disabled={readOnly || saveDraft === undefined}
          loading={setMutation.isPending}
        >
          {t("settings.retries.saveButton")}
        </Button>
        <Button
          variant="default"
          onClick={() => {
            setMutation.reset();
            resetMutation.mutate();
          }}
          disabled={readOnly || !isOverride}
          loading={resetMutation.isPending}
        >
          {t("settings.retries.resetButton")}
        </Button>
      </Group>
    </Stack>
  );
}
